import React from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowUpRight, BellSlash, PencilSimple, X} from '../icons';
import {ChannelLogo} from '../channels/ChannelLogo';
import {formatFullMoney, relativeTimeLabel} from '../client/panel_v11/panelFormatters';
import {ALERTS_UNCONTROLLED, PARAM_FILTER} from '../alerts_center/alertsCenterPaths';
import {FILTER_UNCOVERED} from '../alerts_center/uncontrolled/uncontrolledModel';
import {accountGroups, campaignKey, completionPct, hasConvertedAmount, hasUnconverted, shownCampaigns} from './uncoveredSpend';
import {UncoveredCampaignRow} from './UncoveredCampaignRow';

const RAW = {interpolation: {escapeValue: false}};

// «Cambiar cómo se eligen» abre la edición de la línea en sus cuentas, que es donde vive la regla.
export function editCampaignsPath(businessUnitId) {
    return `/business-unit/${businessUnitId}/accounts`;
}

export const UNCOVERED_CENTER_PATH = `${ALERTS_UNCONTROLLED}?${PARAM_FILTER}=${FILTER_UNCOVERED}`;

/**
 * El contenido del popover del chip de la línea: lo que SU regla deja afuera (la fuga de la regla, caso
 * Kendal), cuánto es contra el presupuesto, con qué regla queda afuera cada cuenta, cuáles son las campañas
 * y las salidas. Va dentro de `AnchoredPopover`.
 *
 * La salida principal es «Cambiar cómo se eligen»: lo que deja afuera una regla por nombre no se arregla
 * sumando campañas a mano (la condición las volvería a dejar afuera). El contenido no escribe: las acciones
 * son del chip, que es quien descuenta lo resuelto.
 */
export function LineUncoveredContent({titleId, uncovered, accounts, businessUnitId, onClose, onDismiss, busyKey,
                                         uncontrolledSpendEnabled}) {
    const {t} = useTranslation();
    const pct = completionPct(uncovered);
    const amountText = formatFullMoney(uncovered.currency, uncovered.amount);
    const totalText = () => {
        if (!hasConvertedAmount(uncovered)) {
            return t('line_uncovered.popover.period_unconverted', {currency: uncovered.currency, ...RAW});
        }
        return pct === null
            ? t('line_uncovered.popover.period_no_budget', {amount: amountText, ...RAW})
            : t('line_uncovered.popover.period', {amount: amountText, pct, ...RAW});
    };
    const groups = accountGroups(uncovered, accounts, t);
    const {shown, more} = shownCampaigns(uncovered);
    const shownKeys = new Set(shown.map(campaignKey));
    // `computed_on` es cuándo corrió el sync que lo calculó; `date`, el día del snapshot (respaldo).
    const updated = relativeTimeLabel(uncovered.computed_on || uncovered.date, t);
    // Con una sola cuenta la regla ya dice de cuál habla: el nombre de la cuenta sería ruido.
    const namesAccounts = groups.length > 1;

    const renderGroup = (group) => {
        const campaigns = group.campaigns.filter((campaign) => shownKeys.has(campaignKey(campaign)));
        return (
            <section className="usp-pop__acct" key={group.key} aria-label={namesAccounts ? group.name : undefined}>
                {namesAccounts && (
                    <p className="usp-pop__acct-name">
                        <span className="usp-logo" aria-hidden="true">
                            <ChannelLogo channel={group.channel} width={14} height={14} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                        </span>
                        <span>{group.name}</span>
                    </p>
                )}
                {group.ruleText && <p className="usp-pop__rule">{group.ruleText}</p>}
                {campaigns.length > 0 && (
                    <ul className="usp-pop__list">
                        {campaigns.map((campaign) => (
                            <UncoveredCampaignRow key={campaignKey(campaign)} campaign={campaign} busyKey={busyKey}
                                                  onDismiss={onDismiss}/>
                        ))}
                    </ul>
                )}
            </section>
        );
    };

    return (
        <>
            <div className="usp-pop__head">
                <h2 className="usp-pop__title" id={titleId}>{t('line_uncovered.popover.title')}</h2>
                <button type="button" className="usp-pop__close" onClick={() => onClose({returnFocus: true})}
                        aria-label={t('line_uncovered.popover.close')}>
                    <X size={16} weight="bold" aria-hidden="true"/>
                </button>
            </div>
            {/* Lo que el backend lista es lo que no mide NINGUNA línea activa de la agencia (las que mide otra
                ya no vienen). Sin decirlo, «No hace falta controlarla» se leía como «ya la controlo en otra». */}
            <p className="usp-pop__nobody">{t('line_uncovered.popover.nobody_measures')}</p>
            {/* Con monto convertido, cuánto y qué parte del presupuesto; si además hay campañas en otra moneda,
                una nota dice que no suman ahí. Sin nada convertido, no se escribe «CLP 0» (C-10): cada
                campaña de la lista ya muestra su monto en la moneda de su cuenta. */}
            <p className="usp-pop__total">
                {totalText()}
            </p>
            {hasConvertedAmount(uncovered) && hasUnconverted(uncovered) && (
                <p className="usp-pop__rule">{t('line_uncovered.popover.unconverted_note')}</p>
            )}

            {groups.map(renderGroup)}
            {more > 0 && <p className="usp-pop__more">{t('line_uncovered.popover.more', {count: more})}</p>}

            <p className="usp-pop__note">
                <BellSlash size={13} weight="bold" aria-hidden="true"/>
                <span>
                    {t('line_uncovered.popover.ok_note')}{' '}
                    {t('line_uncovered.popover.later_note')}
                </span>
            </p>
            <div className="usp-pop__acts">
                <Link className="usp-btn -primary" to={editCampaignsPath(businessUnitId)}
                      onClick={() => onClose({returnFocus: false})}>
                    <PencilSimple size={14} weight="bold" aria-hidden="true"/>
                    {t('line_uncovered.popover.edit_rule')}
                </Link>
                {/* «Ver en Inversión sin control»: la solapa tiene permiso propio (toda la 2.1, UD-5); sin él
                    (o en el clásico) no existe. */}
                {uncontrolledSpendEnabled && (
                    <Link className="usp-pop__link" to={UNCOVERED_CENTER_PATH} onClick={() => onClose({returnFocus: false})}>
                        {t('line_uncovered.popover.center')}
                        <ArrowUpRight size={13} weight="bold" aria-hidden="true"/>
                    </Link>
                )}
            </div>
            {updated && (
                <p className="usp-pop__updated">{t('line_uncovered.popover.updated', {when: updated, ...RAW})}</p>
            )}
        </>
    );
}

export default LineUncoveredContent;
